import {
  ChangeDetectionStrategy,
  Component,
  contentChild,
  input,
} from '@angular/core';

/**
 * Padding convention comes from the Tailwind Plus blocks and is the same on every card:
 * body px-4 py-5 sm:p-6, header px-4 py-5 sm:px-6, footer px-4 py-4 sm:px-6.
 *
 * Header and footer only render when something is projected into them, so a plain card
 * emits one div rather than three.
 *
 * A slot needs BOTH markers: `cardHeader` is the attribute `ng-content` selects on, and
 * `#cardHeader` is the reference `contentChild` finds. With only the attribute the
 * content projects into a block the `@if` never renders, so the header disappears with
 * no error — which is exactly how /admin lost two headings and its paginator.
 *
 *   <div #cardHeader cardHeader>Lessen</div>
 *
 * No outline on the card itself: it is already a lighter surface than the page, and a
 * border on top of a background change is the one border worth deleting. The header and
 * footer rules stay, because there the surface is the same on both sides of the line.
 */
@Component({
  // `display: block` on the host: an Angular component element is inline by default,
  // and Tailwind's space-y-* sets margin-top, which does nothing on an inline box. As
  // an inline host this rendered with no vertical gap at all between siblings.
  host: { class: 'block' },
  selector: 'app-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="overflow-hidden rounded-card bg-surface-card"
      [class]="tone() === 'danger' ? 'outline-2 outline-danger/50' : ''"
    >
      @if (header()) {
        <div class="border-b border-border/60 px-4 py-5 sm:px-6">
          <ng-content select="[cardHeader]" />
        </div>
      }
      <div class="px-4 py-5 sm:p-6"><ng-content /></div>
      @if (footer()) {
        <div class="border-t border-border/60 px-4 py-4 sm:px-6">
          <ng-content select="[cardFooter]" />
        </div>
      }
    </div>
  `,
})
export class CardComponent {
  /**
   * `danger` rings the card instead of the caller wrapping it in a second bordered div,
   * which was a box inside a box with two different radii.
   */
  readonly tone = input<'default' | 'danger'>('default');

  protected readonly header = contentChild('cardHeader');
  protected readonly footer = contentChild('cardFooter');
}
